import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Radio,
  PlayArrow,
  SignalCellularAlt,
} from '@mui/icons-material';
import { SectionTabs } from '../../components/SectionTabs';
import { SCHEDULING_TABS, SCHEDULING_ACCENT_COLOR } from './schedulingTabs';
import { schedulingHelp } from '../../components/PageHelp/helpContent';

const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#f59e0b', '#22c55e', '#10b981',
  '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899',
];

const STREAM_FORMATS = [
  { value: 'mp3', label: 'MP3' },
  { value: 'aac', label: 'AAC' },
  { value: 'ogg', label: 'OGG Vorbis' },
  { value: 'flac', label: 'FLAC' },
];

interface Channel {
  id: string;
  name: string;
  description?: string;
  color: string;
  stream_url: string;
  backup_stream_url?: string;
  stream_format: 'mp3' | 'aac' | 'ogg' | 'flac';
  bitrate: number;
  is_active: boolean;
}

// Placeholder data since we don't have channels API yet
const mockChannels: Channel[] = [];

const ChannelsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [channels] = useState<Channel[]>(mockChannels);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#ec4899',
    stream_url: '',
    backup_stream_url: '',
    stream_format: 'mp3' as 'mp3' | 'aac' | 'ogg' | 'flac',
    bitrate: 128,
    is_active: true,
  });

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      description: '',
      color: '#ec4899',
      stream_url: '',
      backup_stream_url: '',
      stream_format: 'mp3',
      bitrate: 128,
      is_active: true,
    });
    setEditingChannel(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (channel: Channel) => {
    setFormData({
      name: channel.name,
      description: channel.description || '',
      color: channel.color,
      stream_url: channel.stream_url,
      backup_stream_url: channel.backup_stream_url || '',
      stream_format: channel.stream_format,
      bitrate: channel.bitrate,
      is_active: channel.is_active,
    });
    setEditingChannel(channel);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    // TODO: Implement channel creation/update
    setCreateDialogOpen(false);
  };

  return (
    <Box>
      <SectionTabs
        title="Scheduling"
        tabs={SCHEDULING_TABS}
        accentColor={SCHEDULING_ACCENT_COLOR}
        help={schedulingHelp.channels}
        actionButton={{
          label: 'New Channel',
          icon: <Add />,
          onClick: handleOpenCreate,
        }}
      />

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search channels..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          size="small"
        />
      </Paper>

      {/* Channels Grid */}
      <Grid container spacing={2}>
        {channels.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase())).map((channel) => (
          <Grid item xs={12} sm={6} md={4} key={channel.id}>
            <Card
              sx={{
                border: '2px solid',
                borderColor: channel.color,
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                opacity: channel.is_active ? 1 : 0.6,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Radio sx={{ color: channel.color }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {channel.name}
                  </Typography>
                  <IconButton size="small" onClick={() => handleOpenEdit(channel)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small">
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                {channel.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {channel.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip
                    size="small"
                    icon={<SignalCellularAlt />}
                    label={`${channel.bitrate}kbps`}
                  />
                  <Chip size="small" label={channel.stream_format.toUpperCase()} />
                  {channel.is_active && (
                    <Chip
                      size="small"
                      icon={<PlayArrow />}
                      label="Live"
                      sx={{ bgcolor: '#10b98120', color: '#10b981' }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {channels.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Radio sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No channels yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add live audio streams for real-time broadcasting
              </Typography>
              <Button variant="contained" onClick={handleOpenCreate}>
                Create Channel
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingChannel ? 'Edit Channel' : 'Create New Channel'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {COLOR_OPTIONS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32, height: 32, borderRadius: 1, bgcolor: color, cursor: 'pointer',
                      border: formData.color === color ? '3px solid white' : 'none',
                      boxShadow: formData.color === color ? `0 0 0 2px ${color}` : 'none',
                    }}
                  />
                ))}
              </Box>
            </Box>
            <TextField
              label="Stream URL"
              value={formData.stream_url}
              onChange={(e) => setFormData({ ...formData, stream_url: e.target.value })}
              fullWidth
              required
              placeholder="https://stream.example.com/live"
            />
            <TextField
              label="Backup Stream URL (optional)"
              value={formData.backup_stream_url}
              onChange={(e) => setFormData({ ...formData, backup_stream_url: e.target.value })}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Format</InputLabel>
                  <Select
                    value={formData.stream_format}
                    label="Format"
                    onChange={(e) => setFormData({ ...formData, stream_format: e.target.value as any })}
                  >
                    {STREAM_FORMATS.map((fmt) => (
                      <MenuItem key={fmt.value} value={fmt.value}>{fmt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Bitrate (kbps)"
                  type="number"
                  value={formData.bitrate}
                  onChange={(e) => setFormData({ ...formData, bitrate: parseInt(e.target.value) || 128 })}
                  fullWidth
                  inputProps={{ min: 64, max: 320 }}
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />}
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name || !formData.stream_url}>
            {editingChannel ? 'Save Changes' : 'Create Channel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChannelsPage;
